import { useEffect, useRef, useState } from 'react';

const ONE_SECOND = 1000;
const ONE_MINUTE = ONE_SECOND * 60;
const ONE_HOUR = ONE_MINUTE * 60;
const ONE_DAY = ONE_HOUR * 24;

export const calculateTimeLeft = (expireAt: string) => {
  const now = new Date().getTime();
  const distance = new Date(expireAt).getTime() - now;
  const hours = Math.floor((distance % ONE_DAY) / ONE_HOUR);
  const minutes = Math.floor((distance % ONE_HOUR) / ONE_MINUTE);
  const seconds = Math.floor((distance % ONE_MINUTE) / ONE_SECOND);
  return {
    hours,
    minutes,
    seconds,
    distance,
  };
};

/**
 * useCountdown hook.
 * @param expireAt - Either an ISO date string OR a plain numeric string representing
 *                   the number of seconds from now (e.g. "175").
 * @param onExpire - Optional callback invoked once when the countdown reaches zero.
 */
const useCountdown = (expireAt: string, onExpire?: () => void) => {
  // If expireAt is a plain number (seconds), compute the future date once on mount.
  const resolvedExpireAt = useRef<string>(expireAt);
  const expiredFired = useRef(false);

  useEffect(() => {
    const asNumber = Number(expireAt);
    if (!isNaN(asNumber) && String(asNumber) === expireAt) {
      // Plain seconds — compute the future timestamp
      resolvedExpireAt.current = new Date(Date.now() + asNumber * ONE_SECOND).toISOString();
    } else {
      resolvedExpireAt.current = expireAt;
    }
    expiredFired.current = false;
  // Only re-run when expireAt itself changes (not on every render)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expireAt]);

  const [timeLeft, setTimeLeft] = useState(() => {
    const asNumber = Number(expireAt);
    const target = !isNaN(asNumber) && String(asNumber) === expireAt
      ? new Date(Date.now() + asNumber * ONE_SECOND).toISOString()
      : expireAt;
    resolvedExpireAt.current = target;
    return calculateTimeLeft(target);
  });

  useEffect(() => {
    const timer = setInterval(() => {
      const updatedTimeLeft = calculateTimeLeft(resolvedExpireAt.current);
      setTimeLeft(updatedTimeLeft);

      // Check if the countdown has expired
      if (updatedTimeLeft.distance < 0) {
        clearInterval(timer);
        if (!expiredFired.current && onExpire) {
          expiredFired.current = true;
          onExpire();
        }
      }
    }, 1000);

    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expireAt, onExpire]);

  return timeLeft;
};

export default useCountdown;
